<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { apiErrorDetail } from '../../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';
import { stepFailure, blockedReason } from '../stepErrors';
import FormField from './FormField.vue';

const REASON_MAX = 2000;

// An administrator puts a ticket into a status by hand, to correct it. It is
// silent: nobody is messaged. So the flow is a reason (required), the call,
// then a "Tell them?" dialog that opens the tenant's and the person in
// charge's chats so the admin can write by hand. The parent shows it while
// it is open and closes it on `close`. `choices` is the server's list for this
// ticket: `allowed` statuses, and `blocked` ones with the reason.
export default {
  name: 'CoLivingSetStatusFlow',
  components: { BaseModal, FormField },
  props: {
    ticketId: { type: String, required: true },
    choices: { type: Object, required: true },
  },
  emits: ['close', 'changed'],
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      status: '',
      reason: '',
      showRequired: false,
      isSaving: false,
      error: '',
      duplicateTicketId: '',
      tell: null,
    };
  },
  computed: {
    max() {
      return REASON_MAX;
    },
    statusLabel() {
      return this.status
        ? this.$t(`CO_LIVING.TICKETS.STATUS.${this.status}`)
        : '';
    },
    hasPeople() {
      return !!this.tell && (!!this.tell.tenant || !!this.tell.pic);
    },
  },
  methods: {
    ...mapActions('coLivingTickets', ['setStatus']),
    blockedReason(code) {
      return blockedReason(this, code);
    },
    async onSubmit() {
      if (!this.status || !this.reason.trim()) {
        this.showRequired = true;
        return;
      }
      this.isSaving = true;
      this.error = '';
      this.duplicateTicketId = '';
      try {
        const result = await this.setStatus({
          id: this.ticketId,
          body: { to: this.status, reason: this.reason.trim() },
        });
        useAlert(
          this.$t('CO_LIVING.TICKETS.SET_STATUS.DONE', {
            status: this.statusLabel,
          })
        );
        this.$emit('changed');
        this.tell = result.tell || {};
        // Nobody to write to: nothing more to ask.
        if (!this.hasPeople) this.$emit('close');
      } catch (err) {
        const { code, message } = stepFailure(this, err);
        this.error = message;
        if (code === 'duplicate_open_ticket') {
          this.duplicateTicketId =
            apiErrorDetail(err).duplicate_ticket_id || '';
        }
        // The menu was stale (another admin moved the ticket): refresh it.
        if (err && err.response && err.response.status === 409) {
          this.$emit('changed');
        }
      } finally {
        this.isSaving = false;
      }
    },
    openTicket(id) {
      this.$emit('close');
      this.$router.push({
        name: 'co_living_ticket',
        params: { accountId: this.$route.params.accountId, ticketId: id },
      });
    },
    openChat(conversationId) {
      this.$emit('close');
      this.$router.push({
        name: 'inbox_conversation',
        params: { conversation_id: conversationId },
      });
    },
  },
};
</script>

<template>
  <BaseModal v-if="!tell" @close="$emit('close')">
    <template #title>
      {{ $t('CO_LIVING.TICKETS.SET_STATUS.MODAL_TITLE') }}
    </template>

    <form
      class="grid grid-cols-1 gap-4"
      data-testid="co-living-set-status-form"
      @submit.prevent="onSubmit"
    >
      <p class="m-0 text-sm text-slate-600 dark:text-slate-300">
        {{ $t('CO_LIVING.TICKETS.SET_STATUS.NOBODY') }}
      </p>
      <FormField
        :label="$t('CO_LIVING.TICKETS.SET_STATUS.STATUS')"
        for-id="co-living-set-status-choice"
        :error="
          showRequired && !status
            ? $t('CO_LIVING.TICKETS.SET_STATUS.STATUS_REQUIRED')
            : ''
        "
      >
        <select
          id="co-living-set-status-choice"
          v-model="status"
          :class="selectClass"
          data-testid="co-living-set-status-choice"
        >
          <option value="" disabled>
            {{ $t('CO_LIVING.TICKETS.SET_STATUS.CHOOSE') }}
          </option>
          <option v-for="item in choices.allowed" :key="item" :value="item">
            {{ $t(`CO_LIVING.TICKETS.STATUS.${item}`) }}
          </option>
          <option
            v-for="item in choices.blocked"
            :key="item.status"
            value=""
            disabled
          >
            {{ $t(`CO_LIVING.TICKETS.STATUS.${item.status}`) }} -
            {{ blockedReason(item.code) }}
          </option>
        </select>
      </FormField>
      <FormField
        :label="$t('CO_LIVING.TICKETS.STEP_MODAL.REASON')"
        for-id="co-living-set-status-reason"
        :error="
          showRequired && !reason.trim()
            ? $t('CO_LIVING.TICKETS.STEP_MODAL.REASON_REQUIRED')
            : error
        "
        :hint="$t('CO_LIVING.TICKETS.STEP_MODAL.REASON_HINT')"
      >
        <textarea
          id="co-living-set-status-reason"
          v-model="reason"
          rows="4"
          :maxlength="max"
          :class="fieldClass"
          data-testid="co-living-set-status-reason"
        />
      </FormField>
      <div
        v-if="duplicateTicketId"
        data-testid="co-living-set-status-duplicate"
      >
        <woot-button
          variant="clear"
          type="button"
          @click="openTicket(duplicateTicketId)"
        >
          {{ $t('CO_LIVING.TICKETS.SET_STATUS.OPEN_DUPLICATE') }}
        </woot-button>
      </div>

      <div class="flex justify-end gap-3">
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{ $t('CO_LIVING.COMMON.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :is-loading="isSaving"
          data-testid="co-living-set-status-confirm"
        >
          {{ $t('CO_LIVING.TICKETS.SET_STATUS.CONFIRM') }}
        </woot-button>
      </div>
    </form>
  </BaseModal>

  <BaseModal v-else @close="$emit('close')">
    <template #title>
      {{ $t('CO_LIVING.TICKETS.SET_STATUS.TELL_TITLE') }}
    </template>

    <div class="grid grid-cols-1 gap-4" data-testid="co-living-tell-them">
      <p class="m-0 text-sm text-slate-600 dark:text-slate-300">
        {{ $t('CO_LIVING.TICKETS.SET_STATUS.TELL_INTRO') }}
      </p>
      <div
        v-for="who in ['tenant', 'pic']"
        :key="who"
        class="flex items-center justify-between gap-3"
      >
        <template v-if="tell[who]">
          <div class="text-sm">
            <p class="m-0 text-xs text-slate-500">
              {{ $t(`CO_LIVING.TICKETS.SET_STATUS.${who.toUpperCase()}`) }}
            </p>
            <p class="m-0 text-slate-900 dark:text-slate-100">
              {{ tell[who].name }}
            </p>
            <p
              v-if="!tell[who].conversation_id"
              class="m-0 text-xs text-slate-500"
              :data-testid="`co-living-tell-phone-${who}`"
            >
              {{ tell[who].phone }}
            </p>
          </div>
          <woot-button
            v-if="tell[who].conversation_id"
            color-scheme="secondary"
            :data-testid="`co-living-tell-chat-${who}`"
            @click="openChat(tell[who].conversation_id)"
          >
            {{ $t('CO_LIVING.TICKETS.SET_STATUS.OPEN_CHAT') }}
          </woot-button>
        </template>
      </div>
      <div class="flex justify-end">
        <woot-button data-testid="co-living-tell-done" @click="$emit('close')">
          {{ $t('CO_LIVING.TICKETS.SET_STATUS.TELL_DONE') }}
        </woot-button>
      </div>
    </div>
  </BaseModal>
</template>
